import { spellLedgerValue, stripValue, writtenReadsFigure } from "@libs/figure-spans";
import type { TLedgerFigure, TLedgerRole, TLedgerSourceKind } from "@/zapi-frontend/shared/types/modelLedger";

export const LEDGER_ROLES: TLedgerRole[] = [
  "objective",
  "decision",
  "constraint",
  "assumption",
  "parameter",
  "intermediate",
  "output"
];

// Where a value came from, as the Source column and its filter read it: a computed figure is
// calculated, a stated one carries the kind of its source, and a source written before sources
// were typed has no kind.
export type TLedgerSourceBucket = "calculated" | TLedgerSourceKind | "other";

export const LEDGER_SOURCE_BUCKETS: TLedgerSourceBucket[] = [
  "calculated",
  "brief",
  "document",
  "web",
  "assumption",
  "other"
];

export type TLegacyMove = { was: number | null; original: string | null };

const LEGACY_MOVE = /^(?:Moved by key in chat|Changed in the memo through chat) \(was ([^)]*)\)\.(?:\s*Originally: (.*))?$/s;

export const legacyMove = (ref: string | null | undefined): TLegacyMove | null => {
  const match = ref ? LEGACY_MOVE.exec(ref) : null;
  if (!match) return null;
  const was = Number(match[1]);
  return { was: Number.isFinite(was) ? was : null, original: match[2]?.trim() || null };
};

export const sourceBucket = (figure: TLedgerFigure): TLedgerSourceBucket =>
  figure.kind === "computed"
    ? "calculated"
    : figure.source?.url && legacyMove(figure.source.ref)
    ? "web"
    : figure.source?.kind ?? "other";

export type TDocumentOrigin = { fileName: string | null; where: string | null };

// A document source is stored as the file the model cited and, after a dash, where in it the
// number was read; the dash is the backend's join, so the first one is the seam.
export const documentOrigin = (ref: string | null): TDocumentOrigin => {
  if (!ref) return { fileName: null, where: null };
  const seam = ref.indexOf(" — ");
  if (seam < 0) return { fileName: ref, where: null };
  return { fileName: ref.slice(0, seam), where: ref.slice(seam + 3).trim() || null };
};

// The role of a figure whose ledger predates roles, by the rule the backend applies when the
// model leaves it out: a computed figure is derived, a step on the way when its key starts with an
// underscore, a posited number is a belief, and any other stated figure is a measured fact.
export const deriveRole = (figure: TLedgerFigure): TLedgerRole | null => {
  if (figure.kind === "relation") return null;
  if (figure.kind === "computed") return figure.key.startsWith("_") ? "intermediate" : "output";
  return figure.source?.kind === "assumption" ? "assumption" : "parameter";
};

const NUMBER_FORMAT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });

export const formatMoveDate = (at: string, language: string): string => {
  const date = new Date(at);
  return Number.isNaN(date.getTime())
    ? at
    : new Intl.DateTimeFormat(language, { day: "numeric", month: "short", year: "numeric" }).format(date);
};

export const formatFigureValue = (value: number | null): string => (value === null ? "—" : NUMBER_FORMAT.format(value));

const CURRENCY_UNIT_RE = /^(usd|eur|gbp|jpy|chf|\$|€|£|¥)\s*(k|thousand|m|mn|mm|million|b|bn|billion)?(?:\s+(.+))?$/i;

export type TValueParts = { number: string; qualifier: string };

// A value the way the table shows it: an amount in a currency unit is spelled as the memo spells a
// figure, with its own places up to dp and the K / M / B step it reaches ("USD M" 4750 reads
// "$4.75B"), a share keeps its percent sign, and any other unit follows the number as a qualifier
// the table may wrap onto its own line.
export const formatValueParts = (value: number | null, unit: string | null, dp?: number | null): TValueParts => {
  if (value === null) return { number: "—", qualifier: "" };
  const text = unit?.trim() ?? "";
  if (!text) return { number: formatFigureValue(value), qualifier: "" };
  if (text.startsWith("%")) return { number: `${formatFigureValue(value)}%`, qualifier: text.slice(1).trim() };
  const currency = CURRENCY_UNIT_RE.exec(text);
  if (!currency) return { number: formatFigureValue(value), qualifier: text };
  const qualifier = currency[3] ?? "";
  const amountUnit = qualifier ? text.slice(0, text.length - qualifier.length).trim() : text;
  return { number: spellLedgerValue(value, amountUnit, dp), qualifier };
};

export const shownValueParts = (text: string, figure: Pick<TLedgerFigure, "value" | "unit" | "dp">): TValueParts => {
  const written = stripValue(text, figure.unit, figure.value)?.figure;
  if (written && figure.value !== null && writtenReadsFigure(written, figure.value, figure.unit, figure.dp)) {
    return { number: written, qualifier: "" };
  }
  return formatValueParts(figure.value, figure.unit, figure.dp);
};

export const formatValue = (value: number | null, unit: string | null, dp?: number | null): string => {
  const { number, qualifier } = formatValueParts(value, unit, dp);
  return qualifier ? `${number} ${qualifier}` : number;
};

export type TFormulaPart =
  | { kind: "ref"; key: string }
  | { kind: "func"; name: string }
  | { kind: "number"; text: string }
  | { kind: "op"; text: string }
  | { kind: "paren"; text: "(" | ")" }
  | { kind: "power"; parts: TFormulaPart[] };

const KEY_AT_RE = /_?[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)*/y;
const NUMBER_AT_RE = /(?:\d[\d_]*(?:\.\d+)?|\.\d+)(?:e[+-]?\d+)?/y;
const OPERATOR_AT_RE = /==|!=|<=|>=|[+\-*/<>,]/y;

const OPERATOR_GLYPHS: Record<string, string> = {
  "*": "x",
  "==": "=",
  "!=": "≠",
  "<=": "≤",
  ">=": "≥"
};

const stickyMatch = (re: RegExp, text: string, at: number): string | null => {
  re.lastIndex = at;
  return re.exec(text)?.[0] ?? null;
};

const closingParen = (text: string, open: number): number => {
  let depth = 0;
  for (let i = open; i < text.length; i += 1) {
    if (text[i] === "(") depth += 1;
    if (text[i] === ")") depth -= 1;
    if (depth === 0) return i;
  }
  return -1;
};

// The operand after `**`: a parenthesised group, kept with its parentheses, or one number or key,
// either with a sign.
const exponentAfter = (formula: string, at: number): { parts: TFormulaPart[]; end: number } | null => {
  let start = at;
  while (formula[start] === " ") start += 1;
  if (formula[start] === "(") {
    const close = closingParen(formula, start);
    if (close < 0) return null;
    return { parts: formulaParts(formula.slice(start, close + 1)), end: close + 1 };
  }
  const signed = formula[start] === "-" || formula[start] === "+" ? 1 : 0;
  const token = stickyMatch(NUMBER_AT_RE, formula, start + signed) ?? stickyMatch(KEY_AT_RE, formula, start + signed);
  if (!token) return null;
  const end = start + signed + token.length;
  return { parts: formulaParts(formula.slice(start, end)), end };
};

// A formula as the table draws it: every key a reference, a name before a parenthesis a
// function, `**` and its operand a power, and the Python operators as the glyphs the design
// uses. Nothing is dropped: an unexpected character stays as an operator.
export const formulaParts = (formula: string): TFormulaPart[] => {
  const parts: TFormulaPart[] = [];
  let at = 0;
  while (at < formula.length) {
    const char = formula[at];
    if (/\s/.test(char)) {
      at += 1;
      continue;
    }
    if (formula.startsWith("**", at)) {
      const exponent = exponentAfter(formula, at + 2);
      if (exponent) {
        parts.push({ kind: "power", parts: exponent.parts });
        at = exponent.end;
      } else {
        parts.push({ kind: "op", text: "^" });
        at += 2;
      }
      continue;
    }
    if (char === "(" || char === ")") {
      parts.push({ kind: "paren", text: char });
      at += 1;
      continue;
    }
    const operator = stickyMatch(OPERATOR_AT_RE, formula, at);
    if (operator) {
      parts.push({ kind: "op", text: OPERATOR_GLYPHS[operator] ?? operator });
      at += operator.length;
      continue;
    }
    const number = stickyMatch(NUMBER_AT_RE, formula, at);
    if (number) {
      parts.push({ kind: "number", text: number });
      at += number.length;
      continue;
    }
    const key = stickyMatch(KEY_AT_RE, formula, at);
    if (key) {
      const callFollows = /^\s*\(/.test(formula.slice(at + key.length));
      parts.push(callFollows ? { kind: "func", name: key } : { kind: "ref", key });
      at += key.length;
      continue;
    }
    parts.push({ kind: "op", text: char });
    at += 1;
  }
  return parts;
};

const URL_RE = /https?:\/\/[^\s)]+/g;

export type TTextPart = { kind: "text"; text: string } | { kind: "link"; url: string };

export const textWithLinks = (text: string): TTextPart[] => {
  const parts: TTextPart[] = [];
  let at = 0;
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    if (start > at) parts.push({ kind: "text", text: text.slice(at, start) });
    parts.push({ kind: "link", url: match[0] });
    at = start + match[0].length;
  }
  if (at < text.length) parts.push({ kind: "text", text: text.slice(at) });
  return parts;
};

// Every word of the query has to appear somewhere in the figure. The key is searched both as
// written and with its dots and underscores turned into spaces, so "client revenue" finds
// `frame.client.revenue.amt.2025` as readily as "frame.client" does; the value is searched as
// the table shows it and as the number it is.
export const figureMatches = (figure: TLedgerFigure, query: string): boolean => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [
    figure.name,
    figure.key,
    figure.key.replace(/[._]+/g, " "),
    figure.group,
    figure.formula,
    figure.source?.kind,
    figure.source?.ref,
    figure.source?.title,
    figure.source?.url,
    figure.unit,
    figure.description,
    figure.role,
    figure.value !== null ? String(figure.value) : null,
    formatValue(figure.value, figure.unit)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
};

export const groupFigures = (figures: TLedgerFigure[]): { name: string; rows: TLedgerFigure[] }[] => {
  const groups: { name: string; rows: TLedgerFigure[] }[] = [];
  const index = new Map<string, number>();
  for (const figure of figures) {
    let at = index.get(figure.group);
    if (at === undefined) {
      at = groups.length;
      index.set(figure.group, at);
      groups.push({ name: figure.group, rows: [] });
    }
    groups[at].rows.push(figure);
  }
  return groups;
};

// What each figure drives: the computed figures whose formula reads it, in ledger order. Read
// off the dependency graph rather than stored, so it cannot drift from the formulas.
export const dependentsOf = (figures: TLedgerFigure[]): Map<string, TLedgerFigure[]> => {
  const dependents = new Map<string, TLedgerFigure[]>();
  for (const figure of figures) {
    if (figure.kind !== "computed") continue;
    for (const key of figure.dependsOn) {
      const list = dependents.get(key);
      if (list) list.push(figure);
      else dependents.set(key, [figure]);
    }
  }
  return dependents;
};
